import type { EntityData } from '@adornis/baseql/entities/types.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { inclusiveInterval } from '@martinus/validation/inclusiveInterval.js';
import { maxLength } from '@martinus/validation/maxLength.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import { BeschaeftigungsTyp, GermanTaxClassification, Konfession } from '../../../../db/enums/misc-enums.js';
import type { Person } from '../../../entities/person.js';

export const EmployeeSectionSteuernForm = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  return html`
    <div class="form-grid">
      ${FormGridEntry({
        label: 'Verfügbarkeit',
        render: DropdownSelectionInput({
          ...props.form.field('mitarbeiterDaten', 'anSteuerInfos', 'verfuegbarkeit'),
          selectables: Object.values(BeschaeftigungsTyp),
          placeholder: 'Haupt- oder Nebenarbeitgeber',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Steueridentifikationsnummer',
        render: TextInput({
          ...props.form.field('mitarbeiterDaten', 'anSteuerInfos', 'steuerIdentNr'),
          placeholder: 'Keine Steueridentifikationsnummer hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Steuerklasse',
        render: DropdownSelectionInput({
          ...props.form.field('mitarbeiterDaten', 'anSteuerInfos', 'steuerklasse'),
          selectables: Object.values(GermanTaxClassification),
          placeholder: 'Keine Steuerklasse hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Steuerklasse des Ehegatten/Lebenspartners',
        render: DropdownSelectionInput({
          ...props.form.field('mitarbeiterDaten', 'anSteuerInfos', 'steuerklassePartner'),
          selectables: Object.values(GermanTaxClassification),
          placeholder: 'Keine Steuerklasse des Ehegatten/Lebenspartners hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${
        props.form.document?.mitarbeiterDaten?.anSteuerInfos?.steuerklasse === '4' ||
        props.form.document?.mitarbeiterDaten?.anSteuerInfos?.steuerklassePartner === '4'
          ? FormGridEntry({
              label: 'Faktor',
              render: NumberInput({
                ...props.form.field('mitarbeiterDaten', 'anSteuerInfos', 'factor'),
                placeholder: 'Kein Faktor hinterlegt',
                placeholderMode: 'hide',
                clearable: true,
              }),
            })
          : nothing
      }
      ${FormGridEntry({
        label: 'Kinderfreibetrag',
        render: NumberInput({
          ...props.form.field('mitarbeiterDaten', 'anSteuerInfos', 'kinderFreibetrag'),
          placeholder: 'Kein Kinderfreibetrag hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
          step: 0.5,
          min: 0,
        }),
      })}
      ${
        props.form.document?.mitarbeiterDaten?.anSteuerInfos?.kinderFreibetrag
          ? FormGridEntry({
              label: 'Kopie des Nachweis-Elterneigenschaft',
              render: FileInput({
                ...props.form.field('mitarbeiterDaten', 'anSteuerInfos', 'elterneigenschaftID'),
                placeholder: 'Keine Kopie des Nachweis-Elterneigenschaft hinterlegt',
                placeholderMode: 'hide',
              }),
            })
          : nothing
      }
      ${FormGridEntry({
        label: 'Konfession des Ehegatten/Lebenspartners',
        render: DropdownSelectionInput({
          ...props.form.field('mitarbeiterDaten', 'anSteuerInfos', 'konfessionPartner'),
          selectables: Object.values(Konfession),
          placeholder: 'Keine Konfession des Ehegatten/Lebenspartners hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      <h3 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">Freibeträge</h3>
      ${FormGridEntry({
        label: 'Jahresfreibetrag',
        render: html`
          <div class="flex flex-row grow gap-4">
            ${NumberInput({
              ...props.form.field('mitarbeiterDaten', 'anSteuerInfos', 'steuerfreibetraege', 'jahresFreibetrag'),
              placeholder: 'Kein Jahresfreibetrag hinterlegt',
              placeholderMode: 'hide',
              clearable: true,
              suffix: '€',
            })}
            ${NumberInput({
              ...props.form.field(
                'mitarbeiterDaten',
                'anSteuerInfos',
                'steuerfreibetraege',
                'jahresHinzurechnungsbetrag',
              ),
              placeholder: 'Jahreshinzurechnungsbetrag',
              placeholderMode: 'prefix',
              clearable: true,
              suffix: '€',
            })}
          </div>
        `,
      })}
      ${FormGridEntry({
        label: 'Monatsfreibetrag',
        render: html`
          <div class="flex flex-row grow gap-4">
            ${NumberInput({
              ...props.form.field('mitarbeiterDaten', 'anSteuerInfos', 'steuerfreibetraege', 'monatsFreibetrag'),
              placeholder: 'Kein Monatsfreibetrag hinterlegt',
              placeholderMode: 'hide',
              clearable: true,
              suffix: '€',
            })}
            ${NumberInput({
              ...props.form.field(
                'mitarbeiterDaten',
                'anSteuerInfos',
                'steuerfreibetraege',
                'monatsHinzurechnungsbetrag',
              ),
              placeholder: 'Monatshinzurechnungsbetrag',
              placeholderMode: 'prefix',
              clearable: true,
              suffix: '€',
            })}
          </div>
        `,
      })}
    </div>
  `;
});

export const EmployeeSectionSteuernFormValidator = {
  verfuegbarkeit: sgNonOptional(),
  steuerIdentNr: maxLength(11, { allowFalsy: true }),
  steuerklasse: sgNonOptional(),
  kinderFreibetrag: inclusiveInterval(0, 99, { allowFalsy: true }),
};
